// Generazione di deeplink "aggiungi evento" per Outlook Web App (outlook.office.com).
// Porting dello Google Apps Script "Outlook Links": stesse colonne, stesso template del corpo.

export const TIMEZONE = "Europe/Rome";

// "Altre informazioni" punta sempre alla guida Microsoft per partecipare a una riunione Teams
export const INFO_URL = "https://support.microsoft.com/en-us/teams/meetings/join-a-meeting-in-microsoft-teams";

const BASE_URL = "https://outlook.office.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent";

// Ordine colonne del foglio di input (A..G), come nello script originale.
// La colonna F (Info URL) è mantenuta solo per compatibilità dei file ed è ignorata: si usa INFO_URL.
export const COLONNE_INPUT = [
  { key: "subject", label: "Subject" },
  { key: "location", label: "Location" },
  { key: "start", label: "Start" },
  { key: "end", label: "End" },
  { key: "teamsUrl", label: "Teams URL" },
  { key: "infoUrl", label: "Info URL" },
  { key: "bodyHtml", label: "Body HTML" },
];

const ERRORE_DATA = 'data/ora non valida (usa "dd/MM/yyyy HH:mm")';

export function safeString(v) {
  if (v == null) return "";
  return String(v).replace(/ /g, " ").trim();
}

function escapeHtml(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Offset (ms) del fuso `timeZone` rispetto a UTC nell'istante `ts`
function offsetFuso(ts, timeZone) {
  const parti = new Intl.DateTimeFormat("en-US", {
    timeZone, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(new Date(ts));
  const p = Object.fromEntries(parti.map((x) => [x.type, Number(x.value)]));
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - ts;
}

// Interpreta un orario "da orologio" come ora locale di `timeZone` e restituisce la Date UTC corrispondente
export function daOraLocale(y, M, d, h = 0, m = 0, s = 0, timeZone = TIMEZONE) {
  const wall = Date.UTC(y, M - 1, d, h, m, s);
  let ts = wall - offsetFuso(wall, timeZone);
  // Secondo passaggio per gli istanti a cavallo del cambio ora legale/solare
  ts = wall - offsetFuso(ts, timeZone);
  return new Date(ts);
}

/**
 * Converte il valore di una cella/campo in Date.
 * Accetta: Date, "dd/MM/yyyy HH:mm[:ss]", "yyyy-MM-ddTHH:mm[:ss]" (datetime-local),
 * stringhe ISO con fuso esplicito, seriali Excel. Gli orari senza fuso sono intesi in Europe/Rome.
 */
export function asDate(v) {
  if (v instanceof Date && !isNaN(v)) return v;

  if (typeof v === "string") {
    const s = v.replace(/ /g, " ").replace(/\s+/g, " ").trim();

    let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (m) {
      const [, dd, MM, yyyy, hh = "0", mm = "0", ss = "0"] = m;
      return verificaData(Number(yyyy), Number(MM), Number(dd), Number(hh), Number(mm), Number(ss));
    }

    m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
    if (m) {
      const [, yyyy, MM, dd, hh = "0", mm = "0", ss = "0"] = m;
      return verificaData(Number(yyyy), Number(MM), Number(dd), Number(hh), Number(mm), Number(ss));
    }

    // ISO con fuso esplicito (Z / +02:00)
    if (/(Z|[+-]\d{2}:?\d{2})$/.test(s)) {
      const d2 = new Date(s);
      if (!isNaN(d2)) return d2;
    }
  }

  if (typeof v === "number" && isFinite(v)) {
    // Seriale Excel: rappresenta l'orario "da orologio", senza fuso
    const w = new Date(Math.round((v - 25569) * 86400 * 1000));
    return daOraLocale(
      w.getUTCFullYear(), w.getUTCMonth() + 1, w.getUTCDate(),
      w.getUTCHours(), w.getUTCMinutes(), w.getUTCSeconds()
    );
  }

  throw new Error(ERRORE_DATA);
}

function verificaData(y, M, d, h, m, s) {
  const ok = M >= 1 && M <= 12 && d >= 1 && d <= 31 && h <= 23 && m <= 59 && s <= 59
    && new Date(Date.UTC(y, M - 1, d)).getUTCDate() === d;
  if (!ok) throw new Error(ERRORE_DATA);
  return daOraLocale(y, M, d, h, m, s);
}

// UTC in formato 'YYYY-MM-DDTHH:mm:ssZ'
export function toUtcStamp(value) {
  return asDate(value).toISOString().replace(/\.\d{3}Z$/, "Z");
}

/** Template corpo HTML (prima riga 16pt grassetto, seconda grassetto, interlinea 1.5em) */
export function buildBodyHtml(teamsUrl) {
  const joinLine = teamsUrl
    ? `<a href="${escapeHtml(teamsUrl)}">Fai clic qui per partecipare all'evento</a>.`
    : `Fai clic qui per partecipare all'evento.`;

  const infoLine = `<a href="${INFO_URL}">Altre informazioni</a>`;

  return [
    `<p style="font-size:16pt; font-weight:bold; line-height:1.5em;">Riunione di Microsoft Teams</p>`,
    `<p style="font-weight:bold; line-height:1.5em;">Partecipa da computer o app per dispositivi mobili.</p>`,
    `<p style="line-height:1.5em;">${joinLine}</p>`,
    `<p style="line-height:1.5em;">${infoLine}</p>`,
  ].join("");
}

/**
 * Genera il deeplink Outlook per un evento.
 * Restituisce { link } oppure { errore } (messaggio leggibile), senza lanciare.
 */
export function generaLinkOutlook(evento) {
  try {
    const subject = safeString(evento.subject);
    const location = safeString(evento.location);
    const teamsUrl = safeString(evento.teamsUrl);
    const customBody = safeString(evento.bodyHtml);
    const start = typeof evento.start === "string" ? safeString(evento.start) : evento.start;
    const end = typeof evento.end === "string" ? safeString(evento.end) : evento.end;

    const mancanti = [];
    if (!subject) mancanti.push("Subject");
    if (!location) mancanti.push("Location");
    if (start == null || start === "") mancanti.push("Start");
    if (end == null || end === "") mancanti.push("End");
    if (mancanti.length) return { errore: `campi mancanti → ${mancanti.join(", ")}` };

    const startUtc = toUtcStamp(start);
    const endUtc = toUtcStamp(end);
    if (endUtc < startUtc) return { errore: "la fine è precedente all'inizio" };

    const bodyHtml = customBody || buildBodyHtml(teamsUrl);

    const link = BASE_URL +
      `&subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(bodyHtml)}` +
      `&location=${encodeURIComponent(location)}` +
      `&startdt=${startUtc}` +
      `&enddt=${endUtc}`;
    return { link };
  } catch (e) {
    return { errore: e.message };
  }
}

// Righe del foglio (array di array, prima riga = intestazione) → eventi con numero di riga del foglio
export function righeToEventi(righe) {
  return righe
    .map((r, idx) => ({ r, riga: idx + 1 }))
    .slice(1)
    .filter(({ r }) => Array.isArray(r) && r.some((c) => safeString(c) !== ""))
    .map(({ r, riga }) => ({
      riga,
      ...Object.fromEntries(COLONNE_INPUT.map((c, i) => [c.key, r[i]])),
    }));
}
